import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Storefront, Sparkle, Smiley, Copy, ArrowLeft, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CustomerReward() {
  const { slug, code } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [outlet, setOutlet] = useState(null);
  const isNoWin = code === "no-win";

  useEffect(() => {
    // Fetch outlet config (to know if multiple spins allowed)
    api.get(`/public/outlet/${slug}`).then((r) => setOutlet(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (isNoWin) {
      setData({
        attempt: location.state?.attempt || { reward_label: "Try Again" },
        outlet_name: location.state?.outletName || "",
      });
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/public/reward/${code}`);
        setData(data);
      } catch {
        toast.error("Reward not found");
      } finally { setLoading(false); }
    })();
  }, [code, isNoWin, location.state]);

  const att = data?.attempt;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copied");
    } catch {}
  };

  const spinAgain = () => {
    // Reset client session so a new attempt can be recorded (backend already allows via allow_multiple_spins)
    localStorage.removeItem(`rs_session_${slug}`);
    localStorage.removeItem(`rs_screenshot_${slug}`);
    navigate(`/r/${slug}`);
  };

  const canSpinAgain = !!outlet?.allow_multiple_spins;

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--ink-muted)]">Loading…</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center">Not found</div>;

  if (isNoWin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5" style={{ background: "var(--bg)" }}>
        <div className="absolute inset-0 dot-bg opacity-30 pointer-events-none" />
        <div className="relative w-full max-w-md text-center">
          <div className="card-pop p-8" style={{ background: "var(--pastel-blue)" }}>
            <Smiley size={56} weight="bold" className="mx-auto" />
            <h1 className="display text-3xl font-black tracking-tighter mt-4">Better luck next time!</h1>
            <p className="text-[var(--ink-muted)] mt-2">Thanks for your feedback — we appreciate it. See you on your next visit.</p>
            <Link to={`/r/${slug}`} data-testid="reward-back-link" className="btn-ghost mt-6 inline-flex">
              <ArrowLeft size={16} weight="bold" /> Back
            </Link>
          </div>
          {canSpinAgain && (
            <div className="mt-5 text-center">
              <button onClick={spinAgain} data-testid="reward-spin-again-btn" className="btn-ghost inline-flex">
                <ArrowClockwise size={16} weight="bold" /> Spin again
              </button>
              <div className="mt-2 text-[11px] text-[var(--ink-muted)]">Testing mode — owner has enabled multiple spins</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-5 py-10 flex items-center justify-center" style={{ background: "var(--bg)" }}>
      <div className="absolute inset-0 dot-bg opacity-30 pointer-events-none" />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <span className="tag" style={{ background: "var(--pastel-yellow)" }}>
            <Sparkle size={12} weight="fill" /> You won!
          </span>
        </div>
        <div className="card-pop p-7 sm:p-9 text-center" style={{ background: "#fff" }}>
          <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Your reward</div>
          <h1 className="display text-5xl font-black tracking-tighter mt-2" style={{ color: "var(--primary)" }} data-testid="reward-label">
            {att.reward_label}
          </h1>
          {data.outlet_name && (
            <div className="mt-4 flex items-center justify-center gap-1.5 text-sm text-[var(--ink-muted)]">
              <Storefront size={14} weight="bold" /> {data.outlet_name}
            </div>
          )}

          <div className="mt-7 rounded-2xl border-[1.5px] border-[var(--ink)] p-5" style={{ background: "var(--secondary)" }}>
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Show staff this code</div>
            <div className="code-display text-3xl sm:text-4xl mt-2" data-testid="reward-code">{att.redemption_code}</div>
            <button onClick={copyCode} data-testid="reward-copy-btn" className="mt-3 text-xs font-bold inline-flex items-center gap-1 hover:underline">
              <Copy size={12} weight="bold" /> Copy code
            </button>
          </div>

          <p className="text-xs text-[var(--ink-muted)] mt-5">
            Show this screen to a staff member at <strong>{data.outlet_name || "the outlet"}</strong> to redeem. Single use.
          </p>
        </div>

        {/* Branding removed */}

        {canSpinAgain && (
          <div className="mt-5 text-center">
            <button onClick={spinAgain} data-testid="reward-spin-again-btn" className="btn-ghost inline-flex">
              <ArrowClockwise size={16} weight="bold" /> Spin again
            </button>
            <div className="mt-2 text-[11px] text-[var(--ink-muted)]">Testing mode — owner has enabled multiple spins</div>
          </div>
        )}
      </div>
    </div>
  );
}
